import './perks-music-detail.component.scss';

import { h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Link } from 'wouter-preact';

import { LegacyAuthModalState } from '../../../../auth/models/auth.model';
import { authenticationFlowService } from '../../../../auth/services/authentication-flow.service';
import { selectIsAuthenticated } from '../../../../auth/store/auth.selectors';
import { SpinnerComponent } from '../../../../common/components/spinner/spinner.component';
import { GatedAccessResponseType } from '../../../../content/models/access-response';
import { useTranslation } from '../../../../i18n';
import { modalService } from '../../../../modal/modal.service';
import { DownloadErrorModal } from '../../../../music/components/download-error-modal/download-error-modal.component';
import { OpenInBrowserModal } from '../../../../music/components/open-in-browser-modal/open-in-browser-modal.component';
import { TracklistComponent } from '../../../../music/components/tracklist/tracklist.component';
import { getAlbumId, isTrackPlayable } from '../../../../music/models/album.helpers';
import type { Album, AudioFormat } from '../../../../music/models/album.interface';
import {
  playTrack,
  restartCurrentTrack,
  setMusicIsPlaying,
  setMusicIsShuffling,
} from '../../../../music/store/music.reducer';
import {
  selectCurrentMusicAlbumId,
  selectCurrentTrackIndex,
  selectMusicAlbum,
  selectMusicAlbumAccess,
  selectMusicAlbumLoading,
  selectMusicHasActivated,
  selectMusicIsPlaying,
  selectMusicIsShuffling,
  selectQueueSource,
} from '../../../../music/store/music.selectors';
import { downloadAlbum, loadAlbum } from '../../../../music/store/music.thunk';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { toastService } from '../../../../toast/toast.service';
import { isInAppBrowser, isMobile } from '../../../../utils/platform.utils';
import { getSearchParams } from '../../../../utils/window.utils';
import { selectAlbums } from '../../../store/perks.selectors';

interface PerksMusicDetailProps {
  albumId: string;
}

export const PerksMusicDetailComponent = ({ albumId }: PerksMusicDetailProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const otaMode = !!getSearchParams()?.get('access');
  const albums = useAppSelector(selectAlbums);
  const albumAccess = useAppSelector(selectMusicAlbumAccess(albumId));
  const musicAlbum = useAppSelector(selectMusicAlbum(albumId));
  const musicAlbumLoading = useAppSelector(selectMusicAlbumLoading(albumId));
  const currentTrackIndex = useAppSelector(selectCurrentTrackIndex);
  const isPlaying = useAppSelector(selectMusicIsPlaying);
  const currentAlbumId = useAppSelector(selectCurrentMusicAlbumId);
  const hasActivated = useAppSelector(selectMusicHasActivated);
  const isShuffle = useAppSelector(selectMusicIsShuffling);
  const queueSource = useAppSelector(selectQueueSource);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  const album = useMemo(
    () => albums?.find((a: Album) => getAlbumId(a) === albumId) ?? musicAlbum ?? null,
    [albums, albumId, musicAlbum]
  );

  useEffect(() => {
    requestAnimationFrame(() => {
      const activeTrack = document.getElementById('active-track');
      if (activeTrack) {
        activeTrack.scrollIntoView({ behavior: 'instant', block: 'center' });
      } else {
        window.scrollTo(0, 0);
      }
    });
  }, [albumId]);

  useEffect(() => {
    if (!otaMode && !isAuthenticated) return;
    const perksAlbum = albums?.find((a: Album) => getAlbumId(a) === albumId);
    if (!perksAlbum && !musicAlbum && !musicAlbumLoading) {
      dispatch(loadAlbum(albumId));
    }
  }, [albumId, otaMode, isAuthenticated]);

  // playPostAudio leaves currentAlbumId pointing at the last album played, and
  // isPlaying is one global flag — without the queueSource check this page
  // showed the post's playing state as its own (SNGL-7299).
  const isThisAlbumActive = hasActivated && queueSource === 'album' && currentAlbumId === albumId;
  const isThisAlbumPlaying = isThisAlbumActive && isPlaying;

  const firstPlayableIndex = useMemo(() => {
    if (!album) return 0;
    const index = [...album.tracks].sort((a, b) => a.order - b.order).findIndex(isTrackPlayable);
    return Math.max(0, index);
  }, [album]);

  const handlePlayTrack = (index: number) => {
    if (isThisAlbumActive && index === currentTrackIndex) {
      dispatch(setMusicIsPlaying(!isPlaying));
    } else {
      dispatch(playTrack({ albumId, index }));
    }
  };

  const handleShuffle = () => {
    dispatch(setMusicIsShuffling(!isShuffle));
  };

  const handleDownload = async ({ format }: AudioFormat) => {
    // In-app browsers can't save the file but the request would still spend a download, so stop
    // before it is made and send the fan to a real browser instead (SNGL-6923).
    if (isInAppBrowser()) {
      modalService.open({ component: OpenInBrowserModal });
      return;
    }
    const result = await dispatch(downloadAlbum({ albumId, format }));
    if (downloadAlbum.fulfilled.match(result)) {
      setHasDownloaded(true);
      // Phone browsers save the zip with little or no visible sign, and fans who think nothing
      // happened tap again, spending another download each time (SNGL-6923).
      if (isMobile()) {
        toastService.success(t('music.downloadStarted'), t('music.downloadStartedHint'));
      }
      window.location.href = result.payload.url;
    } else if (downloadAlbum.rejected.match(result)) {
      modalService.open({
        component: DownloadErrorModal,
        props: { message: result.payload },
      });
    }
  };

  const isExpiredOtaLink =
    otaMode && albumAccess?.accessResponse === GatedAccessResponseType.REQUEST_LOGIN;

  useEffect(() => {
    if (isExpiredOtaLink && isAuthenticated) {
      const url = new URL(window.location.href);
      url.searchParams.delete('access');
      window.location.replace(url.toString());
    }
  }, [isExpiredOtaLink, isAuthenticated]);

  const handleLogin = async () => {
    const returnToUrl = (() => {
      const url = new URL(window.location.href);
      url.searchParams.delete('access');
      return url.toString();
    })();
    await authenticationFlowService.initiateAuthentication({
      state: LegacyAuthModalState.LOGIN,
      allowedStates: [LegacyAuthModalState.LOGIN, LegacyAuthModalState.RESET_PASSWORD, LegacyAuthModalState.CREATE_ACCOUNT],
      returnToUrl,
    });
  };

  const isUnauthWithNoAccess = !isAuthenticated && !otaMode && !album;

  // While the OTA album access check is in flight, show a spinner so there's
  // no blank flash between the parent's loading state clearing and this
  // component knowing whether the link is expired or valid.
  if (otaMode && musicAlbumLoading && !isExpiredOtaLink)
    return (
      <section class="single-loading">
        <SpinnerComponent />
      </section>
    );

  if (!album && !isExpiredOtaLink && !isUnauthWithNoAccess) return null;

  return (
    <div class={`single-music-detail ${otaMode ? 'ota-mode' : ''}`}>
      {isAuthenticated && !otaMode && albums?.length !== 1 && (
        <div class="go-back">
          <Link to="/music">
            <span>{t('perks.backToLibrary')}</span>
          </Link>
        </div>
      )}

      {isExpiredOtaLink || isUnauthWithNoAccess ? (
        <div class="no-access">
          {isExpiredOtaLink && <h2>{t('music.linkExpired')}</h2>}
          <p>{t('music.loginToAccess')}</p>
          <button class="btn button" onClick={handleLogin}>
            {t('auth.logIn')}
          </button>
        </div>
      ) : (
        <TracklistComponent
          album={album}
          currentTrackIndex={isThisAlbumActive ? currentTrackIndex : -1}
          isPlaying={isThisAlbumPlaying}
          isShuffle={isShuffle}
          onPlayAll={() =>
            isThisAlbumActive
              ? dispatch(setMusicIsPlaying(!isThisAlbumPlaying))
              : handlePlayTrack(firstPlayableIndex)
          }
          onPlayTrack={handlePlayTrack}
          onTogglePlay={() => dispatch(setMusicIsPlaying(!isThisAlbumPlaying))}
          onShuffle={handleShuffle}
          onDownload={!otaMode || !hasDownloaded ? handleDownload : undefined}
          onLoginToDownload={otaMode && hasDownloaded ? handleLogin : undefined}
        />
      )}
    </div>
  );
};
